class CartDrawer extends HTMLElement {
  constructor() {
    super()

    this._onKeyUp = (evt) => evt.code === 'Escape' && this.close()
    this._onOverlayClick = (e) => {
      e.preventDefault()
      this.close()
    }

    this.addEventListener('keyup', this._onKeyUp)

    this.overlayEl = this.querySelector('#CartDrawer-Overlay')
    if (this.overlayEl) {
      this.overlayEl.addEventListener('click', this._onOverlayClick, { passive: false })
    }

    this.setHeaderCartIconAccessibility()
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble')
    if (!cartLink) return

    if (cartLink.__cartDrawerBound) return
    cartLink.__cartDrawerBound = true

    cartLink.setAttribute('role', 'button')
    cartLink.setAttribute('aria-haspopup', 'dialog')

    cartLink.addEventListener('click', (event) => {
      event.preventDefault()
      this.open(cartLink)
    })

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault()
        this.open(cartLink)
      }
    })
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy)

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary')
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote)
    }

    requestAnimationFrame(() => {
      this.classList.add('animate')
      requestAnimationFrame(() => {
        this.classList.add('active')
      })
    })

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer')

        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close')
        trapFocus(containerToTrapFocusOn, focusElement)
      },
      { once: true }
    )

    document.body.classList.add('overflow-hidden')
  }

  close() {
    this.classList.remove('active')
    removeTrapFocus(this.activeElement)
    document.body.classList.remove('overflow-hidden')
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button')
    cartDrawerNote.setAttribute('aria-expanded', 'false')

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id)
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      )
    })

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape)
  }

  async renderContents(parsedState) {
    if (!parsedState) return

    const drawerHasLines = !!this.querySelector('cart-drawer-items tr.cart-item')
    const serverHasLines = Array.isArray(parsedState.items) && parsedState.items.length > 0
    const isAdd = serverHasLines && !drawerHasLines

    let state = parsedState

    // Fallback: add-to-cart responses may miss `sections`
    if (!state.sections) {
      try {
        const sRes = await fetch('/?sections=cart-drawer,cart-icon-bubble', {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        })
        const sections = await sRes.json()

        state = {
          ...state,
          sections: {
            'cart-drawer': sections['cart-drawer'],
            'cart-icon-bubble': sections['cart-icon-bubble'],
          },
          item_count: typeof state.item_count === 'number' ? state.item_count : window?.cart?.item_count ?? 0,
        }
      } catch (e) {
        console.warn('[CartDrawer] sections fallback failed', e)
        return
      }
    }

    const itemsQty = Array.isArray(state.items)
      ? state.items.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0)
      : null

    let isEmpty =
      itemsQty !== null
        ? itemsQty === 0
        : Number(state.item_count || 0) === 0

    this.patchFromSections(state.sections)

    const domHasRows = this.querySelectorAll('cart-drawer-items tr.cart-item').length > 0
    isEmpty = !domHasRows

    if (isAdd) isEmpty = false

    const scroller = this.querySelector('#CartDrawer') || this.querySelector('.drawer__inner')
    if (scroller) scroller.scrollTop = 0

    this.classList.toggle('is-empty', isEmpty)

    const root = document.getElementById('CartDrawer')
    if (root) root.classList.toggle('is-empty', isEmpty)

    const inner = this.querySelector('#CartDrawer') || this.querySelector('.drawer__inner')
    if (inner) inner.classList.toggle('is-empty', isEmpty)

    requestAnimationFrame(() => {
      this._syncEmptyUI(isEmpty)
    })

    if (!this.classList.contains('active')) this.open()
  }

  // Required by Dawn product-form.js (add-to-cart flow)
  getSectionsToRender() {
    return [
      { id: 'cart-drawer', section: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ]
  }

  _parseToFragment(html) {
    const tpl = document.createElement('template')
    tpl.innerHTML = html
    return tpl.content
  }

  patchFromSections(sections) {
    const drawerHtml = sections?.['cart-drawer']
    if (drawerHtml) {
      const frag = this._parseToFragment(drawerHtml)

      const newInner = frag.querySelector('.drawer__inner')
      const curInner = this.querySelector('.drawer__inner')

      if (newInner && curInner) {
        curInner.innerHTML = newInner.innerHTML
        // Keep id/class on current node, replace only content
      }

      const newFooter = frag.querySelector('.drawer__footer')
      const curFooter = this.querySelector('.drawer__footer')
      if (newFooter && curFooter) {
        curFooter.innerHTML = newFooter.innerHTML
      }

      this._rebindOverlayAfterPatch()
    }

    const bubbleHtml = sections?.['cart-icon-bubble']
    if (bubbleHtml) {
      const frag = this._parseToFragment(bubbleHtml)
      const newBubble = frag.querySelector('.shopify-section') || frag.querySelector('#cart-icon-bubble')

      const currentBubble = document.getElementById('cart-icon-bubble')
      if (newBubble && currentBubble) currentBubble.innerHTML = newBubble.innerHTML
    }
  }

  _syncEmptyUI(isEmpty) {
    // Dawn handles visibility with classes; do not hard-hide non-empty inner
    const root = this.querySelector('#CartDrawer') || this.querySelector('.drawer__inner')
    const emptyInner = this.querySelector('.drawer__inner-empty')

    this.classList.toggle('is-empty', isEmpty)
    if (root) root.classList.toggle('is-empty', isEmpty)

    // Only force display for empty block to avoid “overlay only” stuck state
    if (emptyInner) emptyInner.style.display = isEmpty ? '' : 'none'
    // Do NOT set root.style.display='none' for non-empty – it breaks the drawer slide-in
    if (root) root.style.display = ''
  }

  _rebindOverlayAfterPatch() {
    const overlay = this.querySelector('#CartDrawer-Overlay')
    if (!overlay) return

    if (overlay.__mmBound) return
    overlay.__mmBound = true

    overlay.addEventListener('click', this._onOverlayClick, { passive: false })
  }

  setActiveElement(element) {
    this.activeElement = element
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer)
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ]
  }

  async updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line)

    const optimisticSnap = window.MMOptimistic?.captureSnapshot({ line, quantity, variantId })

    const body = JSON.stringify({
      line,
      quantity,
      sections: ['cart-drawer', 'cart-icon-bubble'],
      sections_url: window.location.pathname,
    })

    try {
      const res = await fetch(`${routes.cart_change_url}`, {
        ...fetchConfig(),
        headers: {
          ...(fetchConfig().headers || {}),
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body,
      })

      let parsedState = await safeJson(res)

      if (!parsedState) {
        this.updateLiveRegions(line, window.cartStrings?.error || 'Error')
        this.onCartUpdate()
        return
      }

      if (!parsedState?.sections) {
        try {
          const sRes = await fetch('/?sections=cart-drawer,cart-icon-bubble', {
            headers: { Accept: 'application/json' },
            cache: 'no-store',
          })
          const sections = await sRes.json()

          parsedState = {
            ...parsedState,
            sections: {
              'cart-drawer': sections['cart-drawer'],
              'cart-icon-bubble': sections['cart-icon-bubble'],
            },
            item_count:
              typeof parsedState?.item_count === 'number'
                ? parsedState.item_count
                : window?.cart?.item_count ?? 0,
          }
        } catch (e) {
          console.warn('[CartDrawerItems] sections fallback failed', e)
        }
      }

      if (!res.ok || parsedState?.errors) {
        this.updateLiveRegions(line, parsedState?.errors || window.cartStrings?.error || 'Error')
        return
      }

      const mustRender = quantity === 0 || parsedState?.item_count === 0
      const skip = mustRender ? false : window.MMOptimistic?.shouldSkipRender(parsedState, optimisticSnap)

      if (!skip) {
        const drawer = document.querySelector('cart-drawer')
        if (drawer?.renderContents) drawer.renderContents(parsedState)
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-drawer-items',
        cartData: parsedState,
        variantId,
      })

      this.updateLiveRegions(line, '')
    } catch (e) {
      console.error(e)
      this.updateLiveRegions(line, window.cartStrings?.error || 'Error')
    } finally {
      this.disableLoading(line)
    }
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems)
}